import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import * as apiProduct from 'api/apiProducts';
import * as apiWarehouse from 'api/apiWarehouses';
import { useModal } from 'context/ModalContext';
import { useSettings } from 'context/SettingsContext';
import { useTheme } from 'context/ThemeContext';
import { useToast } from 'context/ToastContext';
import { selectStylesDark, selectStylesLigth } from 'utils/helpers/structs/selectStyles';
import type { ProductSelectOption, SelectOption } from '../../types';
import { toOption } from '../helper';
import { openVendorFilterDlg as showVendorFilterDlg } from './openVendorFilterDlg';
import type { ProductsSelectProps } from './types';

const normalizeVendorId = (raw: number | string | null | undefined): number | null => {
    const id = Number(raw);
    return Number.isFinite(id) && id > 0 ? id : null;
};

const useProductsSelect = ({
    title,
    onChange,
    autoChangeId,
    value,
    onlyWarehouseId = null,
    forceIdVendor = null,
    disabled = false,
    setIsLoading,
    required = true,
    preload = false,
}: ProductsSelectProps) => {
    const { t } = useTranslation(['Products', 'common']);
    const { showToast } = useToast();
    const { showDlg } = useModal();
    const { localSettings } = useSettings();
    const { darkTheme } = useTheme();
    const [search, setSearch] = useState('');
    const [options, setOptions] = useState<ProductSelectOption[]>([]);
    const [selected, setSelected] = useState<ProductSelectOption | null>(null);
    const [searched, setSearched] = useState(false);
    const [loading, setLoading] = useState(false);
    const [vendorFilterId, setVendorFilterId] = useState<number | null>(null);
    const [selectTheme] = useState(darkTheme ? selectStylesDark : selectStylesLigth);
    const warehouseIdsRef = useRef<number[] | null>(null);
    const selectedIdRef = useRef<number | string | null>(null);
    const autoAppliedRef = useRef<string | number | null>(null);
    const loadGenRef = useRef(0);
    const onChangeRef = useRef(onChange);
    const vendorFilterIdRef = useRef<number | null>(null);
    onChangeRef.current = onChange;
    selectedIdRef.current = selected?.value ?? null;

    const forcedVendorId = normalizeVendorId(forceIdVendor);
    const isVendorFilterLocked = forcedVendorId != null;
    const effectiveVendorFilterId = forcedVendorId ?? vendorFilterId;
    vendorFilterIdRef.current = effectiveVendorFilterId;

    const isFixed = Boolean(selected);
    const showResults = loading || searched;
    const canChangeVendorFilter = !isFixed && !isVendorFilterLocked;

    const vendorOptions = useMemo<SelectOption[]>(
        () =>
            (localSettings?.DIRS?.Vendor || [])
                .filter((vendor) => !vendor.hidden || vendor.ID === effectiveVendorFilterId)
                .map((vendor) => ({
                    value: vendor.ID,
                    label: vendor.vendorName,
                })),
        [localSettings?.DIRS?.Vendor, effectiveVendorFilterId],
    );

    const vendorFilterLabel =
        effectiveVendorFilterId != null
            ? vendorOptions.find((vendor) => vendor.value === effectiveVendorFilterId)?.label ||
              t('selectAllVendors')
            : t('selectAllVendors');

    const setLoadingBoth = (next: boolean) => {
        setLoading(next);
        if (setIsLoading) setIsLoading(next);
    };

    const beginLoad = () => {
        loadGenRef.current += 1;
        const gen = loadGenRef.current;
        setLoadingBoth(true);
        return gen;
    };

    const endLoad = (gen: number) => {
        if (gen !== loadGenRef.current) return;
        setLoadingBoth(false);
    };

    const getWarehouseIds = async (): Promise<number[] | null> => {
        if (onlyWarehouseId === null || onlyWarehouseId === undefined) return null;
        if (warehouseIdsRef.current) return warehouseIdsRef.current;
        const ids = await apiWarehouse.getProductsInWarehouse(onlyWarehouseId);
        warehouseIdsRef.current = Array.isArray(ids) ? ids.map(Number).filter((id) => id > 0) : [];
        return warehouseIdsRef.current;
    };

    const resolveProductOption = async (
        productId: number | string,
    ): Promise<ProductSelectOption | null> => {
        const normalizedId = Number(productId);
        if (!Number.isFinite(normalizedId) || normalizedId <= 0) return null;
        const warehouseIds = await getWarehouseIds();
        if (warehouseIds !== null && !warehouseIds.includes(normalizedId)) {
            return null;
        }
        const product = await apiProduct.getProduct(normalizedId);
        return product ? toOption(product) : null;
    };

    const fetchProducts = async (nextVendorFilterId?: number | null) => {
        if (disabled || loading || isFixed) return;

        const vendorId =
            forcedVendorId ??
            (nextVendorFilterId !== undefined ? nextVendorFilterId : vendorFilterIdRef.current);
        const gen = beginLoad();

        const warehouseIds = await getWarehouseIds();
        if (warehouseIds !== null && warehouseIds.length === 0) {
            setOptions([]);
            setSearched(true);
            endLoad(gen);
            return;
        }

        const res = await apiProduct.getList({
            search: (search || '').trim(),
            page: 1,
            onPage: 100,
            status: [1],
            order: 'order by prod_name',
            ...(warehouseIds !== null ? { Ids: warehouseIds } : {}),
            ...(vendorId != null && vendorId > 0 ? { vendors: [vendorId] } : {}),
        });

        if (gen !== loadGenRef.current) return;
        setSearched(true);
        endLoad(gen);

        if (!res) {
            setOptions([]);
            showToast(t('common:messError'), 'danger');
            return;
        }

        setOptions((res.products || []).map(toOption));
    };

    const applyVendorFilter = (nextId: number | null) => {
        if (isVendorFilterLocked) return;
        setVendorFilterId(nextId);
        setOptions([]);
        setSearched(false);
    };

    const openVendorFilterDlg = () => {
        if (disabled || isVendorFilterLocked) return;
        showVendorFilterDlg({
            showDlg,
            t,
            initialVendorId: vendorFilterIdRef.current,
            vendorOptions,
            selectTheme,
            onApply: applyVendorFilter,
        });
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            fetchProducts();
        }
    };

    const handleSelect = (option: ProductSelectOption) => {
        if (disabled) return;
        setSelected(option);
        onChange?.(option);
        setSearch('');
        setOptions([]);
        setSearched(false);
    };

    const handleClear = () => {
        if (disabled) return;
        setSelected(null);
        onChange?.(null);
        setSearch('');
        setOptions([]);
        setSearched(false);
    };

    const handleCloseResults = () => {
        if (disabled || loading) return;
        setOptions([]);
        setSearched(false);
    };

    useEffect(() => {
        warehouseIdsRef.current = null;
    }, [onlyWarehouseId]);

    useEffect(() => {
        if (!isVendorFilterLocked) return;
        setVendorFilterId(null);
        setOptions([]);
        setSearched(false);
    }, [forcedVendorId, isVendorFilterLocked]);

    useEffect(() => {
        if (preload) return;

        const syncFromValue = async () => {
            if (value == null || value === '') {
                setSelected(null);
                return;
            }
            if (Number(selectedIdRef.current) === Number(value)) return;

            const gen = beginLoad();
            const option = await resolveProductOption(value);
            if (gen !== loadGenRef.current) return;
            setSelected(option);
            endLoad(gen);
        };

        void syncFromValue();
    }, [value, onlyWarehouseId, preload]);

    useEffect(() => {
        if (preload) return;

        const normalizedAutoId = Number(autoChangeId);
        if (!Number.isFinite(normalizedAutoId) || normalizedAutoId <= 0) {
            autoAppliedRef.current = null;
            return;
        }
        if (Number(autoAppliedRef.current) === normalizedAutoId) return;

        autoAppliedRef.current = normalizedAutoId;

        const applyAuto = async () => {
            const gen = beginLoad();
            const option = await resolveProductOption(normalizedAutoId);
            if (gen !== loadGenRef.current) return;
            setSelected(option);
            onChangeRef.current?.(option);
            endLoad(gen);
            if (!option) {
                showToast(t('selectNoResults'), 'warning');
            }
        };

        void applyAuto();
    }, [autoChangeId, onlyWarehouseId, preload]);

    return {
        preload,
        labelText: title || t('selectTitle'),
        disabled,
        required,
        search,
        setSearch,
        options,
        selected,
        loading,
        isFixed,
        showResults,
        vendorFilterId: effectiveVendorFilterId,
        vendorFilterLabel,
        canChangeVendorFilter,
        isVendorFilterLocked,
        fetchProducts,
        openVendorFilterDlg,
        handleKeyDown,
        handleSelect,
        handleClear,
        handleCloseResults,
        t,
    };
};

export default useProductsSelect;
